import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, filter, switchMap, take, Subject, map, forkJoin, catchError, of } from 'rxjs';
import { environment } from '@environments/environment';
import { Licencia, Local, LocalCanal, LocalCanalConfiguracion, LocalForm } from '@core/models';
import {
  ResponseSuccess,
  ResponseObjectSuccess,
} from '@interfaces/response-success';
import { Store } from '@ngrx/store';
import { selectCredential, selectLicenseList } from '@store/selectors';
import { Credential, LicenciaResumenForm } from '@core/models';
import { transformArrayLicenseData, transformLicenseUpsert, transformSelectMenuItem, transformStoreModeUpsert } from '@core/helpers';
import { MenuItem } from 'primeng/api';
import { LicenseFilter } from '../store/interfaces/license-filter';
import { LocalChannelEnum } from '@core/enums';
import { StoreChannelConfigurationService } from '../store/store-channel-configuration/store-channel-configuration.service';

@Injectable({ providedIn: 'root' })
export class LicenseService {
  credential$!: Observable<Credential | null>;
  licenseList$: Observable<Licencia[] | null>;

  private upsertCompletedSource = new Subject<void>();
  upsertCompleted$ = this.upsertCompletedSource.asObservable();

  private closeCompletedSource = new Subject<void>();
  closeCompleted$ = this.closeCompletedSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store,
    private storeChannelConfigurationService: StoreChannelConfigurationService,
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.licenseList$ = this.store.select(selectLicenseList);
  }

  upsertCompleted(): void {
    this.upsertCompletedSource.next();
    this.closeCompleted();
  }

  closeCompleted(): void {
    this.closeCompletedSource.next();
  }

  find(id: string): Observable<ResponseSuccess<Licencia>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Licencia>>(
          `${environment.microSeguridades}/api/v1/pos/licencias/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  findAll({ name, channelId, storeId }: LicenseFilter): Observable<ResponseSuccess<Licencia>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {
          ...(!!name && { name }),
          ...(!!channelId && { localCanalId: channelId }),
          ...(!!storeId && { tiendaId: storeId }),
        };

        return this.http.get<ResponseSuccess<Licencia>>(
          `${environment.microSeguridades}/api/v1/pos/licencias`,
          {
            headers: { Authorization: token },
            params,
          }
        );
      }),
      take(1)
    );
  }

  upsert(
    storeForm: LocalForm,
    isNewStore: boolean = false,
  ): Observable<ResponseObjectSuccess<Local>> {
    const { id: localId, licenses = [] } = storeForm;

    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted &&
        !!licenses && !!licenses.length
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return forkJoin(
          licenses.map(({ totalRemovedLicenses = [] }) => {
            return !!totalRemovedLicenses.length
              ? forkJoin(
                  totalRemovedLicenses.map((id) => 
                    this.http.delete<Response>(
                      `${environment.microSeguridades}/api/v1/pos/dispositivos/${id}`,
                      { headers: { Authorization: token } }
                    ).pipe(
                      catchError(error => {
                        console.error('Error removing device:', id, error);
                        return of({ 
                          success: false 
                        } as ResponseObjectSuccess<LocalCanal>);
                      })
                    )
                  )
                )
              : [of({ success: true, message: 'No devices to remove' })]
          })
        ).pipe(
          switchMap(() => {
            return forkJoin(
              !!isNewStore
                ? [this.storeChannelConfigurationService.findAll({ localId, section: LocalChannelEnum.all })]
                : [of({ success: true, data: [], count: 0 })]
            ).pipe(
              switchMap(([ { data  } ]) => {
                const licensesWithLocalCanalId = !!isNewStore ? this.updateStoreChannelLicense(data, licenses) : licenses;
                const licensesUpsert = transformLicenseUpsert(licensesWithLocalCanalId);
                return forkJoin([
                  !!licensesUpsert && !!licensesUpsert.length
                    ? this.http.patch<ResponseObjectSuccess<Licencia>>(
                        `${environment.microSeguridades}/api/v1/pos/licencias/local/${localId}`,
                        licensesUpsert,
                        { headers: { Authorization: token } }
                      ).pipe(
                        switchMap(() =>
                          forkJoin(
                            (licensesWithLocalCanalId ?? []).map(({ localCanalId, channelId, enabled }) =>
                              !!localCanalId  
                                ? this.http.patch<ResponseObjectSuccess<LocalCanal>>(
                                    `${environment.microConfiguraciones}/api/v1/local-canal/${localCanalId}`,
                                    { habilitado: enabled, localId },
                                    { headers: { Authorization: token } }
                                  ).pipe(
                                    catchError(error => {
                                      console.error('Error updating store channel!', localCanalId, error);
                                      return of({ 
                                        success: false 
                                      } as ResponseObjectSuccess<LocalCanal>);
                                    })
                                  )
                                : !! enabled
                                  ? this.http.post<ResponseObjectSuccess<LocalCanal>>(
                                      `${environment.microConfiguraciones}/api/v1/local-canal`,
                                      { habilitado: enabled, localId, canalId: channelId },
                                      { headers: { Authorization: token } }
                                    ).pipe(
                                      catchError(error => {
                                        console.error('Error creating store channel!', localCanalId, error);
                                        return of({ 
                                          success: false 
                                        } as ResponseObjectSuccess<LocalCanal>);
                                      })
                                    )
                                  : of([{} as Observable<ResponseObjectSuccess<LocalCanal>>])
                            )
                          )
                        ),
                      )
                : of([{} as Observable<ResponseObjectSuccess<Licencia>>])
                ]).pipe(
                  switchMap(() =>
                    this.http.patch<ResponseObjectSuccess<Local>>(
                      `${environment.microConfiguraciones}/api/v1/locales/${localId}/modo-licenciamiento`,
                      { modo: transformStoreModeUpsert(storeForm) },
                      { headers: { Authorization: token } }
                    )
                  )
                )
              })
            )
          }),
          catchError(error => {
            console.error('Error updating license!', error);
            return of({ 
              success: false 
            } as ResponseObjectSuccess<Local>);
          })
        );
      }),
      take(1)
    );
  }

  updateStoreChannelLicense(storeChannels: LocalCanalConfiguracion[], licenseSummaries: LicenciaResumenForm[] ): LicenciaResumenForm[] {
    return licenseSummaries.map((licenseSummary) => {
      const storeChannel = (storeChannels ?? []).find((storeChannel) => storeChannel.canalId === licenseSummary.channelId);
      return {
        ...licenseSummary,
        localCanalId: storeChannel?.idLocalCanal as string,
      };
    });
  }
    
  remove(id: string): Observable<ResponseObjectSuccess<Response>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.delete<ResponseObjectSuccess<Response>>(
          `${environment.microSeguridades}/api/v1/pos/licencias/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  getDropdown(filterId?: string): Observable<MenuItem[]> {
    return this.licenseList$.pipe(
      filter((licenses): licenses is Licencia[] => !!licenses),
      map((licenses) => ((licenses ?? []).filter((license) => (license.localCanalId === filterId || !filterId)))),
      map((licenses) => 
        transformSelectMenuItem(transformArrayLicenseData(licenses), 'idLicencia', 'nombre', 'dispositivoId')
      ),
    );
  }
}
